<script>
  import { link } from 'svelte-spa-router';
  import { tick } from 'svelte';
  import {
    ArrowLeft, Download, AlertTriangle, TrendingUp, TrendingDown, Minus, Upload,
  } from '../lib/icons/index.js';
  import Layout from '../components/Layout.svelte';
  import ErrorBanner from '../components/ErrorBanner.svelte';
  import SuccessBanner from '../components/SuccessBanner.svelte';
  import InfoHint from '../components/InfoHint.svelte';
  import PageLoader from '../components/PageLoader.svelte';
  import Spinner from '../components/Spinner.svelte';
  import BilancioValue from '../components/BilancioValue.svelte';
  import BilancioUploadSheet from '../components/BilancioUploadSheet.svelte';
  import BilancioIsa from '../components/BilancioIsa.svelte';
  import { bilanciApi } from '../stores/api.js';
  import { activeCompanyId } from '../stores/company.js';
  import { capabilities } from '../stores/capabilities.js';
  import { t, tImmediate } from '../i18n/index.js';
  import { formatDate, formatDateTime } from '../lib/labels.js';
  import {
    DEFAULT_ALIQUOTA_IVA, ESITO_BADGE, bilancioPermissions, describeCell, differenceDirection, documentKey,
    fileNeedsRepair, formatDecimal, formatMoney, indentLevel, parseAliquotaIva, textParagraphs,
    visibleStatementLines,
  } from '../lib/bilancio.js';

  /** Route params: `id` del bilancio. */
  export let params = {};

  const TABS = ['sintesi', 'prospetti', 'riscontro', 'nota', 'isa'];
  /** Gli indici in evidenza in cima alla sintesi. */
  const KPI_IDS = ['ricavi', 'mol', 'ebit', 'liquidita'];
  /** Gli indici della tabella «Andamento negli anni». */
  const SERIES_IDS = ['ricavi', 'valore_produzione', 'mol', 'ebit', 'ros', 'roe', 'indipendenza', 'current_ratio', 'liquidita'];

  let detail = null;
  let loading = true;
  let error = null;
  let tab = 'sintesi';

  let analisi = null;
  let analisiLoading = false;
  let analisiError = null;
  let aliquotaInput = DEFAULT_ALIQUOTA_IVA;
  let aliquotaApplied = DEFAULT_ALIQUOTA_IVA;
  let aliquotaError = '';

  let riscontro = null;
  let riscontroLoading = false;
  let riscontroError = null;

  let isaOpened = false;

  let showUnknown = false;
  let downloadError = null;
  let uploadOpen = false;
  let success = '';

  $: companyId = $activeCompanyId;
  $: bilancioId = params?.id;
  $: permissions = bilancioPermissions($capabilities?.role);
  $: if (companyId && bilancioId) open(companyId, bilancioId);

  $: bilancio = detail?.bilancio ?? null;
  $: statements = detail?.statements ?? null;
  $: hasPrevious = Boolean(bilancio?.prevPeriodEnd);
  $: year = bilancio?.fiscalYear;
  $: warnings = uniqueWarnings([...(bilancio?.warnings ?? []), ...(statements?.warnings ?? [])]);
  $: indici = analisi?.analisi?.indici ?? [];
  $: testi = analisi?.analisi?.testi ?? {};
  $: groups = Object.keys(testi.gruppi ?? {});
  $: kpis = KPI_IDS.map((id) => indici.find((entry) => entry.id === id)).filter(Boolean);
  $: serie = analisi?.analisi?.serie ?? [];
  $: seriesRows = SERIES_IDS.map((id) => indici.find((entry) => entry.id === id)).filter(Boolean);
  $: rtesti = riscontro?.riscontro?.testi ?? {};

  let loadedFor = '';
  async function open(company, id) {
    const key = `${company}:${id}`;
    if (key === loadedFor) return;
    loadedFor = key;
    detail = null;
    analisi = null;
    riscontro = null;
    error = null;
    analisiError = null;
    riscontroError = null;
    downloadError = null;
    aliquotaInput = DEFAULT_ALIQUOTA_IVA;
    aliquotaApplied = DEFAULT_ALIQUOTA_IVA;
    aliquotaError = '';
    tab = 'sintesi';
    isaOpened = false;
    loading = true;
    try {
      const response = await bilanciApi.get(company, id);
      if (key !== loadedFor) return;
      detail = response;
    } catch (err) {
      if (key !== loadedFor) return;
      error = err;
    } finally {
      if (key === loadedFor) loading = false;
    }
    if (detail) loadAnalisi();
  }

  async function loadAnalisi() {
    const key = loadedFor;
    analisiLoading = true;
    analisiError = null;
    try {
      const response = await bilanciApi.analisi(companyId, bilancioId, { aliquotaIva: aliquotaApplied });
      if (key === loadedFor) analisi = response;
    } catch (err) {
      if (key === loadedFor) analisiError = err;
    } finally {
      if (key === loadedFor) analisiLoading = false;
    }
  }

  async function loadRiscontro() {
    const key = loadedFor;
    riscontroLoading = true;
    riscontroError = null;
    try {
      const response = await bilanciApi.riscontroFatture(companyId, bilancioId);
      if (key === loadedFor) riscontro = response;
    } catch (err) {
      if (key === loadedFor) riscontroError = err;
    } finally {
      if (key === loadedFor) riscontroLoading = false;
    }
  }

  function selectTab(next) {
    tab = next;
    if (next === 'riscontro' && !riscontro && !riscontroLoading) loadRiscontro();
    // The ISA tab reads the invoices of the whole year: only once it is opened.
    if (next === 'isa') isaOpened = true;
  }

  function onTabKeydown(event) {
    const index = TABS.indexOf(tab);
    let next = null;
    if (event.key === 'ArrowRight') next = TABS[(index + 1) % TABS.length];
    else if (event.key === 'ArrowLeft') next = TABS[(index - 1 + TABS.length) % TABS.length];
    else if (event.key === 'Home') next = TABS[0];
    else if (event.key === 'End') next = TABS[TABS.length - 1];
    if (!next) return;
    event.preventDefault();
    selectTab(next);
    tick().then(() => document.getElementById(`bilancio-tab-${next}`)?.focus());
  }

  function applyAliquota() {
    const value = parseAliquotaIva(aliquotaInput);
    if (value === null) {
      aliquotaError = tImmediate('bilancio.sintesi.assunzioni.invalid');
      return;
    }
    aliquotaError = '';
    if (value === aliquotaApplied && analisi) return;
    aliquotaApplied = value;
    loadAnalisi();
  }

  function uniqueWarnings(list) {
    const seen = new Set();
    return list.filter((warning) => {
      const key = `${warning.code}|${warning.concept ?? ''}|${warning.periodRole ?? ''}|${warning.contextRef ?? ''}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  function saveBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  }

  async function download() {
    downloadError = null;
    try {
      const { blob, filename } = await bilanciApi.downloadFile(companyId, bilancioId);
      saveBlob(blob, filename);
    } catch (err) {
      // The warning below the header explains a copy that failed its check.
      if (err.code === 'bilancio_file_integrity' && detail) {
        detail = { ...detail, bilancio: { ...detail.bilancio, fileStatus: err.fileStatus || 'missing' } };
      } else {
        downloadError = err;
      }
    }
  }

  function onRepaired(event) {
    uploadOpen = false;
    const { outcome, bilancio: updated } = event.detail;
    success = outcome === 'restored'
      ? tImmediate('bilancio.upload.done.restored', { year })
      : tImmediate('bilancio.upload.done.unchanged', { year });
    if (updated && detail) detail = { ...detail, bilancio: { ...detail.bilancio, ...updated } };
  }

  /** Il testo di un motivo: la frase del server se è un codice noto, altrimenti la frase stessa. */
  const reasonText = (reason) => (reason ? (testi.motivi?.[reason] ?? reason) : '');
  const period = (start, end) => $t('bilancio.list.period', { start: formatDate(start), end: formatDate(end) });
  const yearHeading = (offset) => $t('bilancio.prospetti.anno', { year: year - offset });
  const isTotalLine = (line) => /^totale\b/i.test(line.label || '') || line.voce === 'A-B';
  const ratioHint = (entry) => [entry.spiegazione, $t(`bilancio.sintesi.direzione.${entry.direzione}`)].filter(Boolean).join(' ');
  const caveatsOf = (entry) => [...new Set([...(entry.corrente?.caveats ?? []), ...(entry.precedente?.caveats ?? [])])];
  const documentiLabel = (n) => (n === 1 ? $t('bilancio.riscontro.documentiOne') : $t('bilancio.riscontro.documenti', { n }));
  /** Documenti sommati con un importo letto in parte o dal totale lordo. */
  const shortfallOf = (sdi) => (sdi?.documentiSenzaRighe ?? 0) + (sdi?.documentiRigheIllegibili ?? 0) + (sdi?.documentiSenzaRiepilogo ?? 0);

  function anagraficaValue(line) {
    if (line.value === null || line.value === undefined) return '—';
    if (line.itemType === 'xbrli:monetaryItemType') return formatMoney(line.value) ?? line.value;
    if (line.itemType === 'xbrli:dateItemType') return formatDate(line.value);
    if (line.itemType === 'xbrli:booleanItemType') return line.value === 'true' ? 'Sì' : 'No';
    return line.value;
  }

  function docLabel(doc) {
    const number = doc.invoiceNumber || $t('bilancio.riscontro.doc.noNumber');
    return [doc.tipoDocumento, number, doc.invoiceDate ? formatDate(doc.invoiceDate) : null].filter(Boolean).join(' · ');
  }
  function docMeta(doc) {
    return [
      doc.direction ? $t(`bilancio.riscontro.doc.direction.${doc.direction}`, undefined, doc.direction) : null,
      doc.origine ? $t(`bilancio.riscontro.doc.origine.${doc.origine}`, undefined, doc.origine) : null,
      doc.divisa && doc.divisa !== 'EUR' ? doc.divisa : null,
      doc.imponibile ? (doc.divisa && doc.divisa !== 'EUR' ? formatDecimal(doc.imponibile, { minFraction: 2 }) : formatMoney(doc.imponibile)) : null,
    ].filter(Boolean).join(' · ');
  }
</script>

<Layout>
  {#if loading}
    <PageLoader />
  {:else if error}
    <ErrorBanner {error} title={$t('bilancio.detail.loadError')} />
    <a href="/bilancio" use:link class="btn-link"><ArrowLeft size={16} aria-hidden="true" />{$t('bilancio.detail.back')}</a>
  {:else if bilancio}
    <div class="view-head">
      <div class="view-head-copy">
        <a href="/bilancio" use:link class="btn-link -ml-1 mb-2"><ArrowLeft size={16} aria-hidden="true" />{$t('bilancio.detail.back')}</a>
        <h1 class="section-title">{$t('bilancio.detail.title', { year })}</h1>
        <p class="view-subtitle">{bilancio.denominazione || bilancio.entityIdentifier}</p>
      </div>
      {#if permissions.rawRead && bilancio.fileStatus === 'ok'}
        <div class="view-actions">
          <button type="button" class="btn btn-secondary gap-2" on:click={download}>
            <Download size={16} aria-hidden="true" />
            {$t('bilancio.actions.download')}
          </button>
        </div>
      {/if}
    </div>

    <dl class="summary-strip bilancio-meta">
      <div class="summary-item">
        <dt class="summary-label">{$t('bilancio.detail.meta.periodo')}</dt>
        <dd class="summary-note">{period(bilancio.periodStart, bilancio.periodEnd)}</dd>
      </div>
      <div class="summary-item">
        <dt class="summary-label">{$t('bilancio.detail.meta.forma')}</dt>
        <dd class="summary-note inline-flex items-center gap-1">
          {$t(`bilancio.forma.${bilancio.schemaType}`)}
          <InfoHint text={$t(`bilancio.formaHelp.${bilancio.schemaType}`)} />
        </dd>
      </div>
      <div class="summary-item">
        <dt class="summary-label">{$t('bilancio.detail.meta.codice')}</dt>
        <dd class="summary-note mono">{bilancio.entityIdentifier}</dd>
      </div>
      <div class="summary-item">
        <dt class="summary-label">{$t('bilancio.detail.meta.caricato')}</dt>
        <dd class="summary-note">{formatDateTime(bilancio.uploadedAt)}</dd>
      </div>
      <div class="summary-item">
        <dt class="summary-label">{$t('bilancio.detail.meta.file')}</dt>
        <dd class="summary-note inline-flex items-center gap-1">
          <span class="badge {bilancio.fileStatus === 'ok' ? 'badge-green' : 'badge-red'}">{$t(`bilancio.fileStatus.${bilancio.fileStatus}`)}</span>
          <InfoHint text={$t(`bilancio.fileStatusHelp.${bilancio.fileStatus}`)} />
        </dd>
      </div>
    </dl>

    <SuccessBanner message={success} />
    <ErrorBanner error={downloadError} />

    {#if !bilancio.live}
      <div class="alert alert-info mb-4" role="status">
        <div class="min-w-0 flex-1">
          <p class="alert-title">{$t('bilancio.detail.replaced.title')}</p>
          <p class="alert-message mt-1">{$t('bilancio.detail.replaced.body')}</p>
          {#if bilancio.supersededBy}
            <a href={`/bilancio/${bilancio.supersededBy}`} use:link class="btn-link -ml-1 mt-1">{$t('bilancio.detail.replaced.link')}</a>
          {/if}
        </div>
      </div>
    {/if}

    {#if fileNeedsRepair(bilancio)}
      <div class="alert alert-warning mb-4" role="status">
        <AlertTriangle size={18} class="alert-mark mt-0.5" aria-hidden="true" />
        <div class="min-w-0 flex-1">
          <p class="alert-title">{$t(`bilancio.fileStatus.${bilancio.fileStatus}`)}</p>
          <p class="alert-message mt-1">
            {permissions.upload && bilancio.live
              ? $t('bilancio.repair.body', { year })
              : $t('bilancio.repair.bodyReadonly', { year })}
          </p>
          {#if permissions.upload && bilancio.live}
            <button type="button" class="btn btn-secondary btn-sm mt-3 gap-2" on:click={() => { success = ''; uploadOpen = true; }}>
              <Upload size={14} aria-hidden="true" />
              {$t('bilancio.actions.repair')}
            </button>
          {/if}
        </div>
      </div>
    {/if}

    <div class="tab-bar mb-6">
      <div class="tab-list bilancio-tabs" role="tablist" aria-label={$t('bilancio.detail.tabs.label')}>
        {#each TABS as name}
          <button type="button" role="tab" id="bilancio-tab-{name}" class="tab-button"
            aria-selected={tab === name} aria-controls="bilancio-panel-{name}" tabindex={tab === name ? 0 : -1}
            on:click={() => selectTab(name)} on:keydown={onTabKeydown}>
            {$t(`bilancio.detail.tabs.${name}`)}
          </button>
        {/each}
      </div>
    </div>

    <!-- ── Sintesi e indici ──────────────────────────────────────────── -->
    <div id="bilancio-panel-sintesi" role="tabpanel" aria-labelledby="bilancio-tab-sintesi" tabindex="0" hidden={tab !== 'sintesi'}>
      {#if analisiLoading && !analisi}
        <Spinner />
      {:else if analisiError}
        <ErrorBanner error={analisiError} title={$t('bilancio.sintesi.loadError')} />
      {:else if analisi}
        <p class="mb-6 text-sm text-muted measure-wide">{testi.avvertenza}</p>

        {#if warnings.length}
          <details class="disclosure mb-6">
            <summary>
              <span class="disclosure-label">{$t('bilancio.detail.warnings.heading')}<span class="tab-count">{warnings.length}</span></span>
            </summary>
            <div class="disclosure-content">
              <p class="text-xs text-muted mb-2">{$t('bilancio.detail.warnings.intro')}</p>
              <ul class="bilancio-bullets">
                {#each warnings as warning}
                  <li>
                    {warning.message}
                    {#if warning.periodRole}
                      <span class="text-xs text-muted">({$t(`bilancio.detail.warnings.periodo.${warning.periodRole}`, undefined, warning.periodRole)})</span>
                    {/if}
                  </li>
                {/each}
              </ul>
            </div>
          </details>
        {/if}

        {#if !hasPrevious}
          <p class="mb-4 text-sm text-muted">{$t('bilancio.sintesi.noPrevious', { year })}</p>
        {/if}

        <section aria-labelledby="bilancio-kpi-title" class="mb-8">
          <h2 id="bilancio-kpi-title" class="sr-only">{$t('bilancio.sintesi.kpiHeading')}</h2>
          <dl class="summary-strip">
            {#each kpis as entry (entry.id)}
              <div class="summary-item">
                <dt class="summary-label inline-flex items-center gap-1">{entry.label}<InfoHint text={ratioHint(entry)} /></dt>
                <dd class="bilancio-kpi-value">
                  <BilancioValue result={entry.corrente} unit={entry.unit} basi={testi.basi} motivi={testi.motivi} inline />
                </dd>
                {#if entry.variazione && entry.variazione.value !== null}
                  <dd class="summary-note inline-flex items-center gap-1">
                    {#if entry.variazione.value > 0}<TrendingUp size={14} aria-hidden="true" />{:else if entry.variazione.value < 0}<TrendingDown size={14} aria-hidden="true" />{:else}<Minus size={14} aria-hidden="true" />{/if}
                    <BilancioValue result={entry.variazione} unit="%" signed basi={testi.basi} motivi={testi.motivi} inline />
                    <span>{$t('bilancio.sintesi.variazione')}</span>
                  </dd>
                {/if}
              </div>
            {/each}
          </dl>
        </section>

        {#each groups as gruppo}
          {@const rows = indici.filter((entry) => entry.gruppo === gruppo)}
          {#if rows.length}
            <section class="mb-10" aria-labelledby="bilancio-gruppo-{gruppo}">
              <div class="section-heading">
                <h2 id="bilancio-gruppo-{gruppo}">{testi.gruppi[gruppo]}</h2>
              </div>

              {#if gruppo === 'cassa'}
                <form class="bilancio-assumption mb-4" on:submit|preventDefault={applyAliquota} novalidate>
                  <p class="font-bold text-heading text-sm">{$t('bilancio.sintesi.assunzioni.heading')}</p>
                  <p id="bilancio-aliquota-hint" class="text-xs text-muted mt-1 measure-wide">{$t('bilancio.sintesi.assunzioni.hint')}</p>
                  <div class="mt-3 flex flex-wrap items-end gap-3">
                    <div>
                      <label class="label" for="bilancio-aliquota">{$t('bilancio.sintesi.assunzioni.label')}</label>
                      <input id="bilancio-aliquota" class="input bilancio-aliquota-input" type="text" inputmode="decimal"
                        bind:value={aliquotaInput} aria-describedby="bilancio-aliquota-hint{aliquotaError ? ' bilancio-aliquota-error' : ''}"
                        aria-invalid={aliquotaError ? 'true' : undefined} />
                    </div>
                    <button type="submit" class="btn btn-secondary btn-sm" disabled={analisiLoading}>
                      {$t('bilancio.sintesi.assunzioni.apply')}
                    </button>
                  </div>
                  {#if aliquotaError}
                    <p id="bilancio-aliquota-error" class="field-error mt-1">{aliquotaError}</p>
                  {:else}
                    <p class="text-xs text-muted mt-1">{$t('bilancio.sintesi.assunzioni.current', { value: formatDecimal(analisi.analisi.assunzioni?.aliquotaIva ?? aliquotaApplied) })}</p>
                  {/if}
                </form>
              {/if}

              <div class="bilancio-table-wrap">
                <table class="table-modern bilancio-ratio-table w-full" role="table">
                  <caption class="sr-only">{testi.gruppi[gruppo]}</caption>
                  <thead class="table-header" role="rowgroup">
                    <tr role="row">
                      <th class="table-cell table-header-cell text-left" scope="col" role="columnheader">{$t('bilancio.sintesi.columns.indice')}</th>
                      <th class="table-cell table-header-cell text-right" scope="col" role="columnheader">{$t('bilancio.sintesi.columns.anno', { year })}</th>
                      {#if hasPrevious}
                        <th class="table-cell table-header-cell text-right" scope="col" role="columnheader">{$t('bilancio.sintesi.columns.anno', { year: year - 1 })}</th>
                        <th class="table-cell table-header-cell text-right" scope="col" role="columnheader">{$t('bilancio.sintesi.columns.variazione')}</th>
                      {/if}
                    </tr>
                  </thead>
                  <tbody role="rowgroup">
                    {#each rows as entry (entry.id)}
                      <tr class="bilancio-row" role="row">
                        <th scope="row" class="table-cell bilancio-ratio-label" role="rowheader">
                          <span class="inline-flex items-center gap-1">{entry.label}<InfoHint text={ratioHint(entry)} /></span>
                          {#each caveatsOf(entry) as caveat}
                            <span class="block text-xs text-muted">{testi.avvertenze?.[caveat] ?? ''}</span>
                          {/each}
                        </th>
                        <td class="table-cell text-right" role="cell" data-label={$t('bilancio.sintesi.columns.anno', { year })}>
                          <BilancioValue result={entry.corrente} unit={entry.unit} basi={testi.basi} motivi={testi.motivi} />
                        </td>
                        {#if hasPrevious}
                          <td class="table-cell text-right" role="cell" data-label={$t('bilancio.sintesi.columns.anno', { year: year - 1 })}>
                            <BilancioValue result={entry.precedente} unit={entry.unit} basi={testi.basi} motivi={testi.motivi} />
                            {#if entry.riclassificato}
                              <span class="bilancio-flag">
                                <span class="badge badge-magenta">{$t('bilancio.sintesi.riclassificato')}</span>
                                <InfoHint text={$t('bilancio.sintesi.riclassificatoHelp')} />
                              </span>
                            {/if}
                          </td>
                          <td class="table-cell text-right" role="cell" data-label={$t('bilancio.sintesi.columns.variazione')}>
                            {#if entry.variazione}
                              <span class="inline-flex items-center gap-1">
                                {#if entry.variazione.value > 0}<TrendingUp size={14} aria-hidden="true" />{:else if entry.variazione.value < 0}<TrendingDown size={14} aria-hidden="true" />{/if}
                                <BilancioValue result={entry.variazione} unit="%" signed basi={testi.basi} motivi={testi.motivi} />
                              </span>
                            {:else}
                              <span class="text-muted" aria-label={$t('bilancio.basi.ndLabel')}>—</span>
                            {/if}
                          </td>
                        {/if}
                      </tr>
                    {/each}
                  </tbody>
                </table>
              </div>
            </section>
          {/if}
        {/each}

        {#if serie.length > 1}
          <section class="mb-10" aria-labelledby="bilancio-serie-title">
            <div class="section-heading">
              <h2 id="bilancio-serie-title">{$t('bilancio.sintesi.serie.heading')}</h2>
            </div>
            <p class="text-sm text-muted mb-3">{$t('bilancio.sintesi.serie.intro')}</p>
            <!-- Years run across: on a phone the table scrolls inside its own
                 box rather than widening the page, and the box takes focus so
                 it can be scrolled from the keyboard. -->
            <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
            <div class="overflow-x-auto bilancio-serie-wrap" role="region" aria-labelledby="bilancio-serie-title" tabindex="0">
              <table class="table-modern bilancio-serie-table">
                <thead class="table-header">
                  <tr>
                    <th class="table-cell table-header-cell text-left" scope="col">{$t('bilancio.sintesi.serie.indice')}</th>
                    {#each serie as point (point.bilancioId)}
                      <th class="table-cell table-header-cell text-right" scope="col">{point.fiscalYear}</th>
                    {/each}
                  </tr>
                </thead>
                <tbody>
                  {#each seriesRows as entry (entry.id)}
                    <tr class="bilancio-row">
                      <th scope="row" class="table-cell bilancio-ratio-label">{entry.label}</th>
                      {#each serie as point (point.bilancioId)}
                        <td class="table-cell text-right">
                          <BilancioValue result={point.valori?.[entry.id]} unit={entry.unit} basi={testi.basi} motivi={testi.motivi} />
                        </td>
                      {/each}
                    </tr>
                  {/each}
                </tbody>
              </table>
            </div>
          </section>
        {/if}

        <section class="mb-6" aria-labelledby="bilancio-basi-title">
          <h2 id="bilancio-basi-title" class="font-bold text-heading text-sm mb-2">{$t('bilancio.basi.legendHeading')}</h2>
          <ul class="bilancio-bullets text-sm text-muted">
            {#each Object.values(testi.basi ?? {}) as text}
              <li>{text}</li>
            {/each}
          </ul>
        </section>
      {/if}
    </div>

    <!-- ── Stato patrimoniale e conto economico ─────────────────────── -->
    <div id="bilancio-panel-prospetti" role="tabpanel" aria-labelledby="bilancio-tab-prospetti" tabindex="0" hidden={tab !== 'prospetti'}>
      {#if statements}
        <label class="mb-4 inline-flex items-center gap-2 text-sm">
          <input type="checkbox" bind:checked={showUnknown} />
          <span>{$t('bilancio.prospetti.showUnknown')}</span>
        </label>

        {#each ['sp', 'ce'] as name}
          {@const view = visibleStatementLines(statements[name], showUnknown)}
          <section class="mb-10" aria-labelledby="bilancio-{name}-title">
            <div class="section-heading">
              <h2 id="bilancio-{name}-title">{$t(`bilancio.prospetti.${name}`)}</h2>
            </div>
            <p class="text-sm text-muted mb-3 measure-wide">{$t(`bilancio.prospetti.${name}Intro`)}</p>
            {#if view.hidden > 0}
              <p class="text-xs text-muted mb-2">{$t('bilancio.prospetti.hiddenUnknown', { n: view.hidden })}</p>
            {/if}
            <div class="bilancio-table-wrap">
              <table class="table-modern bilancio-statement w-full" role="table">
                <caption class="sr-only">{$t(`bilancio.prospetti.${name}`)}</caption>
                <thead class="table-header" role="rowgroup">
                  <tr role="row">
                    <th class="table-cell table-header-cell text-left" scope="col" role="columnheader">{$t('bilancio.prospetti.voce')}</th>
                    <th class="table-cell table-header-cell text-right" scope="col" role="columnheader">{yearHeading(0)}</th>
                    {#if hasPrevious}
                      <th class="table-cell table-header-cell text-right" scope="col" role="columnheader">{yearHeading(1)}</th>
                    {/if}
                  </tr>
                </thead>
                <tbody role="rowgroup">
                  {#each view.lines as line, index (`${line.concept}-${index}`)}
                    {#if !(line.abstract && line.depth === 0)}
                      <tr class="bilancio-row" class:bilancio-heading-row={line.abstract} class:bilancio-total-row={isTotalLine(line)}
                        role="row" style="--bilancio-depth: {indentLevel(line.depth - 1)}">
                        <th scope="row" class="table-cell bilancio-voce" role="rowheader">
                          {#if line.voce}<span class="mono bilancio-voce-code">{line.voce}</span>{/if}
                          <span>{line.label}</span>
                          {#if line.gruppo}
                            <span class="bilancio-flag">
                              <span class="badge badge-gray">{$t('bilancio.prospetti.cell.gruppo')}</span>
                              <InfoHint text={$t('bilancio.prospetti.cellHelp.gruppo')} />
                            </span>
                          {/if}
                        </th>
                        {#each hasPrevious ? [line.corrente, line.precedente] : [line.corrente] as cell, column}
                          {@const c = describeCell(cell)}
                          <td class="table-cell text-right bilancio-cell" role="cell" data-label={yearHeading(column)}>
                            {#if c.kind === 'heading'}
                              <span class="sr-only">—</span>
                            {:else if c.kind === 'unknown'}
                              <span class="bilancio-cell-muted">{$t('bilancio.prospetti.cell.nonEsposta')}</span>
                              <InfoHint text={c.ipotesiZero
                                ? `${$t('bilancio.prospetti.cellHelp.nonEsposta')} ${$t('bilancio.prospetti.cellHelp.ipotesiZero')}`
                                : $t('bilancio.prospetti.cellHelp.nonEsposta')} />
                            {:else if c.kind === 'nil'}
                              <span class="bilancio-cell-muted">{$t('bilancio.prospetti.cell.vuota')}</span>
                              <InfoHint text={$t('bilancio.prospetti.cellHelp.vuota')} />
                            {:else}
                              <span class="bilancio-cell-figure" class:bilancio-cell-uncertain={c.kind === 'solved'}>{c.text}</span>
                              {#if c.kind === 'solved'}
                                <!-- Said in words under the figure, not as a chip: it has to
                                     fit a third of a phone screen, and it is a sentence. -->
                                <span class="bilancio-cell-detail bilancio-solved">
                                  <span>{[$t('bilancio.prospetti.cell.ricavata'), c.plusMinus ? $t('bilancio.basi.plusMinus', { value: c.plusMinus }) : ''].filter(Boolean).join(' ')}</span>
                                  <InfoHint text={$t('bilancio.prospetti.cellHelp.ricavata')} />
                                </span>
                              {/if}
                            {/if}
                            {#if c.nonRiconciliato}
                              <span class="bilancio-flag">
                                <span class="badge badge-red">{$t('bilancio.prospetti.cell.nonRiconciliato')}</span>
                                <InfoHint text={$t('bilancio.prospetti.cellHelp.nonRiconciliato')} />
                              </span>
                            {/if}
                          </td>
                        {/each}
                      </tr>
                    {/if}
                  {/each}
                </tbody>
              </table>
            </div>
          </section>
        {/each}

        {#if statements.derivati}
          <section class="mb-6" aria-labelledby="bilancio-derivati-title">
            <div class="section-heading">
              <h2 id="bilancio-derivati-title">{$t('bilancio.prospetti.derivati.heading')}</h2>
            </div>
            <p class="text-sm text-muted mb-3 measure-wide">{$t('bilancio.prospetti.derivati.intro')}</p>
            <ul class="bilancio-derived">
              {#each ['B10ab', 'B9c'] as key}
                {@const item = statements.derivati[key]?.corrente}
                {#if item}
                  <li>
                    <p class="font-bold text-heading text-sm">{$t(`bilancio.prospetti.derivati.${key}`)}</p>
                    {#if item.value !== null && item.value !== undefined}
                      {@const c = describeCell(item)}
                      <p class="text-sm">
                        <span class="bilancio-cell-figure">{c.text}</span>
                        {#if c.plusMinus}<span class="bilancio-cell-detail">{$t('bilancio.basi.plusMinus', { value: c.plusMinus })}</span>{/if}
                        {#if item.path}<span class="text-xs text-muted">— {$t(`bilancio.prospetti.derivati.path.${item.path}`, undefined, item.path)}</span>{/if}
                      </p>
                    {:else}
                      <p class="text-sm text-muted">{$t('bilancio.basi.nd')} — {reasonText(item.reason)}</p>
                    {/if}
                    {#if item.caveat}<p class="text-xs text-muted">{item.caveat}</p>{/if}
                  </li>
                {/if}
              {/each}
            </ul>
          </section>
        {/if}
      {/if}
    </div>

    <!-- ── Confronto con le fatture ─────────────────────────────────── -->
    <div id="bilancio-panel-riscontro" role="tabpanel" aria-labelledby="bilancio-tab-riscontro" tabindex="0" hidden={tab !== 'riscontro'}>
      {#if riscontroLoading}
        <Spinner />
      {:else if riscontroError}
        <ErrorBanner error={riscontroError} title={$t('bilancio.riscontro.loadError')} />
      {:else if riscontro}
        {@const r = riscontro.riscontro}
        <p class="text-sm text-muted measure-wide">{rtesti.avvertenza}</p>
        <p class="mt-2 mb-6 text-xs text-muted">
          {$t('bilancio.riscontro.periodo', { start: formatDate(r.periodo.inizio), end: formatDate(r.periodo.fine) })} — {rtesti.base}
        </p>

        {#if r.limiteRaggiunto}
          <div class="alert alert-danger mb-6" role="alert">
            <AlertTriangle size={18} class="alert-mark mt-0.5" aria-hidden="true" />
            <p class="alert-message">{$t('bilancio.riscontro.limite')}</p>
          </div>
        {/if}

        <div class="bilancio-crosscheck mb-10">
          {#each r.confronti as confronto (confronto.id)}
            {@const direction = differenceDirection(confronto.differenza)}
            <article class="card p-5 bilancio-crosscheck-card" aria-labelledby="bilancio-cc-{confronto.id}">
              <header class="flex flex-wrap items-start justify-between gap-2">
                <h3 id="bilancio-cc-{confronto.id}" class="font-bold text-heading">{confronto.label}</h3>
                <span class="badge {ESITO_BADGE[confronto.esito] ?? 'badge-gray'}">{$t(`bilancio.riscontro.esito.${confronto.esito}`)}</span>
              </header>
              <p class="mt-2 text-xs text-muted">{confronto.descrizione}</p>

              <dl class="bilancio-figures mt-4">
                <div>
                  <dt>{$t('bilancio.riscontro.bilancio')}</dt>
                  <dd><BilancioValue result={confronto.bilancio} unit="€" basi={rtesti.basi} motivi={rtesti.motiviBilancio} inline /></dd>
                </div>
                <div>
                  <dt>{$t('bilancio.riscontro.sdi')}</dt>
                  <dd>
                    <span class="bilancio-cell-figure">{formatMoney(confronto.sdi.importo)}</span>
                    <span class="block text-xs text-muted">{documentiLabel(confronto.sdi.documenti ?? 0)}</span>
                  </dd>
                </div>
                <div>
                  <dt class="inline-flex items-center gap-1">{$t('bilancio.riscontro.differenza')}<InfoHint text={$t('bilancio.riscontro.differenzaHint')} /></dt>
                  <dd>
                    {#if confronto.differenza !== null && confronto.differenza !== undefined}
                      <span class="inline-flex items-center gap-1 bilancio-cell-figure">
                        {#if direction === 'up'}<TrendingUp size={14} aria-hidden="true" />{:else if direction === 'down'}<TrendingDown size={14} aria-hidden="true" />{:else}<Minus size={14} aria-hidden="true" />{/if}
                        {formatMoney(confronto.differenza, { signed: true })}
                      </span>
                      {#if direction}<span class="block text-xs text-muted">{$t(`bilancio.riscontro.direction.${direction}`)}</span>{/if}
                    {:else}
                      <span class="text-muted">{$t('bilancio.basi.nd')}</span>
                    {/if}
                  </dd>
                </div>
                <div>
                  <dt>{$t('bilancio.riscontro.tolleranza')}</dt>
                  <dd>{confronto.tolleranza ? $t('bilancio.basi.plusMinus', { value: formatMoney(confronto.tolleranza) }) : '—'}</dd>
                </div>
              </dl>

              {#if confronto.id === 'K1'}
                <ul class="mt-3 text-xs text-muted space-y-1">
                  <li>{$t('bilancio.riscontro.k1.fornitori')}: {formatMoney(confronto.sdi.fornitori)}</li>
                  <li>{$t('bilancio.riscontro.k1.autofatture')}: {formatMoney(confronto.sdi.autofatture)}</li>
                  {#if confronto.sdi.autofattureNonRisolte > 0}
                    <li>{$t('bilancio.riscontro.k1.nonRisolte', { n: confronto.sdi.autofattureNonRisolte })}</li>
                  {/if}
                </ul>
              {/if}
              {#if confronto.sdi.importoAmbiguo && Number(confronto.sdi.importoAmbiguo) !== 0}
                <p class="mt-2 text-xs text-warning">{$t('bilancio.riscontro.importoAmbiguo', { importo: formatMoney(confronto.sdi.importoAmbiguo) })}</p>
              {/if}
              {#if shortfallOf(confronto.sdi) > 0}
                <p class="mt-2 text-xs text-warning">
                  {shortfallOf(confronto.sdi) === 1
                    ? $t('bilancio.riscontro.importiIncompletiOne')
                    : $t('bilancio.riscontro.importiIncompleti', { n: shortfallOf(confronto.sdi) })}
                </p>
              {/if}
              {#if confronto.id === 'C1' && confronto.sdi.documentiSenzaRate > 0}
                <p class="mt-2 text-xs text-muted">
                  {confronto.sdi.documentiSenzaRate === 1
                    ? $t('bilancio.riscontro.c1.senzaRateOne', { importo: formatMoney(confronto.sdi.importoSenzaRate) })
                    : $t('bilancio.riscontro.c1.senzaRate', { n: confronto.sdi.documentiSenzaRate, importo: formatMoney(confronto.sdi.importoSenzaRate) })}
                </p>
              {/if}

              <p class="mt-4 text-sm">{rtesti.esiti?.[confronto.esito]}</p>
              {#if confronto.motivi?.length}
                <ul class="bilancio-bullets mt-2 text-sm">
                  {#each confronto.motivi as motivo}<li>{rtesti.motivi?.[motivo] ?? motivo}</li>{/each}
                </ul>
              {/if}
              {#each confronto.bilancio?.caveats ?? [] as caveat}
                <p class="mt-2 text-xs text-muted">{rtesti.avvertenze?.[caveat] ?? ''}</p>
              {/each}
            </article>
          {/each}
        </div>

        <section class="mb-10" aria-labelledby="bilancio-informative-title">
          <div class="section-heading">
            <h2 id="bilancio-informative-title">{$t('bilancio.riscontro.informative.heading')}</h2>
          </div>
          <p class="text-sm text-muted mb-3 measure-wide">{$t('bilancio.riscontro.informative.intro')}</p>
          <dl class="bilancio-informative">
            <div>
              <dt class="inline-flex items-center gap-1">{$t('bilancio.riscontro.informative.labels.V1')}<InfoHint text={rtesti.informative?.V1} /></dt>
              <dd>
                {formatMoney(r.informative.V1?.ivaVendite)}
                {#if r.informative.V1?.incompleto}<span class="block text-xs text-warning">{$t('bilancio.riscontro.incompleto')}</span>{/if}
              </dd>
            </div>
            <div>
              <dt class="inline-flex items-center gap-1">{$t('bilancio.riscontro.informative.labels.N1')}<InfoHint text={rtesti.informative?.N1} /></dt>
              <dd>
                {formatMoney(r.informative.N1?.importo)}
                {#if r.informative.N1?.incompleto}<span class="block text-xs text-warning">{$t('bilancio.riscontro.incompleto')}</span>{/if}
              </dd>
            </div>
            {#each ['TD26', 'TD27', 'ricevuteTD26TD27'] as key}
              {#if r.informative[key]?.documenti > 0}
                <div>
                  <dt class="inline-flex items-center gap-1">{$t(`bilancio.riscontro.informative.labels.${key}`)}<InfoHint text={rtesti.informative?.[key]} /></dt>
                  <dd>
                    {formatMoney(r.informative[key].importo)} <span class="block text-xs text-muted">{documentiLabel(r.informative[key].documenti)}</span>
                    {#if r.informative[key].incompleto}<span class="block text-xs text-warning">{$t('bilancio.riscontro.incompleto')}</span>{/if}
                  </dd>
                </div>
              {/if}
            {/each}
            <div>
              <dt class="inline-flex items-center gap-1">{$t('bilancio.riscontro.informative.labels.amministratoriCompensi')}<InfoHint text={rtesti.informative?.amministratoriCompensi} /></dt>
              <dd><BilancioValue result={r.informative.amministratoriCompensi} unit="€" basi={rtesti.basi} motivi={rtesti.motiviBilancio} inline /></dd>
            </div>
            <div>
              <dt class="inline-flex items-center gap-1">{$t('bilancio.riscontro.informative.labels.K2')}<InfoHint text={rtesti.informative?.K2} /></dt>
              <dd><BilancioValue result={r.informative.K2} unit="€" basi={rtesti.basi} motivi={rtesti.motiviBilancio} inline /></dd>
            </div>
          </dl>
          {#if r.informative.td20Regolarizzazioni?.length}
            <p class="mt-4 text-sm font-bold text-heading inline-flex items-center gap-1">
              {$t('bilancio.riscontro.informative.labels.td20Regolarizzazioni')}<InfoHint text={rtesti.informative?.td20Regolarizzazioni} />
            </p>
            <ul class="bilancio-docs">
              {#each r.informative.td20Regolarizzazioni as pair}
                <li>
                  <span class="text-xs text-muted">{$t('bilancio.riscontro.liste.autofattura')}:</span>
                  <a href={`/invoices/${pair.autofattura.id}`} use:link class="invoice-link">{docLabel(pair.autofattura)}</a>
                  <span class="text-xs text-muted">· {$t('bilancio.riscontro.liste.originale')}:</span>
                  <a href={`/invoices/${pair.originale.id}`} use:link class="invoice-link">{docLabel(pair.originale)}</a>
                </li>
              {/each}
            </ul>
          {/if}
        </section>

        <section class="mb-10" aria-labelledby="bilancio-spiegazioni-title">
          <div class="section-heading">
            <h2 id="bilancio-spiegazioni-title">{$t('bilancio.riscontro.spiegazioni.heading')}</h2>
          </div>
          <p class="text-sm text-muted mb-4 measure-wide">{$t('bilancio.riscontro.spiegazioni.intro')}</p>
          <div class="bilancio-explanations">
            {#each ['ricavi', 'costi', 'crediti'] as group}
              <div>
                <h3 class="font-bold text-heading text-sm mb-2">{$t(`bilancio.riscontro.spiegazioni.${group}`)}</h3>
                <ul class="bilancio-bullets text-sm">
                  {#each r.spiegazioni[group] ?? [] as item}
                    <li>
                      {item.testo}
                      {#if item.documenti > 0}
                        <span class="block text-xs text-muted">
                          {#if item.giorni && item.importo}
                            {$t('bilancio.riscontro.spiegazioni.figuraGiorni', { giorni: item.giorni, documenti: documentiLabel(item.documenti), importo: formatMoney(item.importo) })}
                          {:else if item.importo}
                            {$t('bilancio.riscontro.spiegazioni.figura', { documenti: documentiLabel(item.documenti), importo: formatMoney(item.importo) })}
                          {:else}
                            {$t('bilancio.riscontro.spiegazioni.conteggio', { documenti: documentiLabel(item.documenti) })}
                          {/if}
                        </span>
                      {/if}
                      {#if item.incompleto}<span class="block text-xs text-warning">{$t('bilancio.riscontro.incompleto')}</span>{/if}
                    </li>
                  {/each}
                </ul>
              </div>
            {/each}
          </div>
        </section>

        <section class="mb-6" aria-labelledby="bilancio-liste-title">
          <div class="section-heading">
            <h2 id="bilancio-liste-title">{$t('bilancio.riscontro.liste.heading')}</h2>
          </div>

          <details class="disclosure">
            <summary><span class="disclosure-label">{$t('bilancio.riscontro.liste.duplicati')}<span class="tab-count">{r.duplicati.length}</span></span></summary>
            <div class="disclosure-content">
              {#if r.duplicati.length}
                <ul class="bilancio-docs">
                  {#each r.duplicati as entry, index (index)}
                    <li>
                      <p class="text-xs text-muted">{[rtesti.duplicati?.[entry.chiave] ?? '', entry.cross ? `(${$t('bilancio.riscontro.liste.cross')})` : ''].filter(Boolean).join(' ')}</p>
                      <p class="text-sm">
                        <span class="text-xs text-muted">{$t('bilancio.riscontro.liste.kept')}:</span>
                        <a href={`/invoices/${entry.kept.id}`} use:link class="invoice-link">{docLabel(entry.kept)}</a>
                        <span class="text-xs text-muted">{docMeta(entry.kept)}</span>
                      </p>
                      {#each entry.dropped as doc (documentKey(doc))}
                        <p class="text-sm">
                          <span class="text-xs text-muted">{$t('bilancio.riscontro.liste.dropped')}:</span>
                          <a href={`/invoices/${doc.id}`} use:link class="invoice-link">{docLabel(doc)}</a>
                          <span class="text-xs text-muted">{docMeta(doc)}</span>
                        </p>
                      {/each}
                      {#if entry.riferimentoSenzaData}<p class="text-xs text-muted">{$t('bilancio.riscontro.liste.riferimentoSenzaData')}</p>{/if}
                    </li>
                  {/each}
                </ul>
              {:else}
                <p class="text-sm text-muted">{$t('bilancio.riscontro.liste.none')}</p>
              {/if}
            </div>
          </details>

          <details class="disclosure" open={r.ambigui.length > 0}>
            <summary><span class="disclosure-label">{$t('bilancio.riscontro.liste.ambigui')}<span class="tab-count">{r.ambigui.length}</span></span></summary>
            <div class="disclosure-content">
              {#if r.ambigui.length}
                <ul class="bilancio-docs">
                  {#each r.ambigui as entry, index (index)}
                    <li>
                      <p class="text-xs text-muted">{rtesti.ambiguita?.[entry.motivo] ?? ''}</p>
                      {#if entry.kept}
                        <p class="text-sm">
                          <span class="text-xs text-muted">{$t('bilancio.riscontro.liste.kept')}:</span>
                          <a href={`/invoices/${entry.kept.id}`} use:link class="invoice-link">{docLabel(entry.kept)}</a>
                          <span class="text-xs text-muted">{docMeta(entry.kept)}</span>
                        </p>
                      {/if}
                      {#if entry.autofattura}
                        <p class="text-sm">
                          <span class="text-xs text-muted">{$t('bilancio.riscontro.liste.autofattura')}:</span>
                          <a href={`/invoices/${entry.autofattura.id}`} use:link class="invoice-link">{docLabel(entry.autofattura)}</a>
                          <span class="text-xs text-muted">{docMeta(entry.autofattura)}</span>
                        </p>
                      {/if}
                      {#each entry.alternative ?? [] as doc (documentKey(doc))}
                        <p class="text-sm">
                          <span class="text-xs text-muted">{$t('bilancio.riscontro.liste.alternative')}:</span>
                          <a href={`/invoices/${doc.id}`} use:link class="invoice-link">{docLabel(doc)}</a>
                          <span class="text-xs text-muted">{docMeta(doc)}</span>
                        </p>
                      {/each}
                      {#if entry.importoAmbiguo}
                        <p class="text-xs text-warning">{$t('bilancio.riscontro.liste.differenza', { importo: formatMoney(entry.importoAmbiguo) })}</p>
                      {/if}
                      {#if entry.mancanti?.length}
                        <p class="text-xs text-warning">{$t('bilancio.riscontro.liste.mancanti', { campi: entry.mancanti.map((field) => $t(`bilancio.riscontro.liste.campi.${field}`)).join(', ') })}</p>
                      {/if}
                    </li>
                  {/each}
                </ul>
              {:else}
                <p class="text-sm text-muted">{$t('bilancio.riscontro.liste.none')}</p>
              {/if}
            </div>
          </details>

          <details class="disclosure" open={(r.importiIncompleti ?? []).length > 0}>
            <summary><span class="disclosure-label">{$t('bilancio.riscontro.liste.importiIncompleti')}<span class="tab-count">{(r.importiIncompleti ?? []).length}</span></span></summary>
            <div class="disclosure-content">
              <p class="text-xs text-muted mb-2">{$t('bilancio.riscontro.liste.importiIncompletiIntro')}</p>
              {#if r.importiIncompleti?.length}
                <ul class="bilancio-docs">
                  {#each r.importiIncompleti as doc, index (`${documentKey(doc)}-${index}`)}
                    <li>
                      <a href={`/invoices/${doc.id}`} use:link class="invoice-link">{docLabel(doc)}</a>
                      <span class="text-xs text-muted">{docMeta(doc)}</span>
                      <p class="text-xs text-muted">{rtesti.importiIncompleti?.[doc.motivo] ?? doc.motivo}</p>
                      {#if doc.motivo === 'riepilogo_non_disponibile' && doc.riepilogo}
                        <p class="text-xs text-muted">{rtesti.riepilogo?.[doc.riepilogo] ?? doc.riepilogo}</p>
                      {/if}
                      {#if doc.righeIllegibili > 0}
                        <p class="text-xs text-warning">{$t('bilancio.riscontro.liste.righeIllegibili', { n: doc.righeIllegibili, righe: doc.righe })}</p>
                      {/if}
                      {#if doc.sezioni?.length}
                        <p class="text-xs text-muted">{$t('bilancio.riscontro.liste.sezioni', { sezioni: doc.sezioni.map((id) => $t(`bilancio.riscontro.sezioni.${id}`)).join(', ') })}</p>
                      {/if}
                    </li>
                  {/each}
                </ul>
              {:else}
                <p class="text-sm text-muted">{$t('bilancio.riscontro.liste.none')}</p>
              {/if}
            </div>
          </details>

          <details class="disclosure">
            <summary><span class="disclosure-label">{$t('bilancio.riscontro.liste.esclusi')}<span class="tab-count">{r.esclusi.length}</span></span></summary>
            <div class="disclosure-content">
              {#if r.esclusi.length}
                <ul class="bilancio-docs">
                  {#each r.esclusi as doc, index (`${documentKey(doc)}-${index}`)}
                    <li>
                      <a href={`/invoices/${doc.id}`} use:link class="invoice-link">{docLabel(doc)}</a>
                      <span class="text-xs text-muted">{docMeta(doc)}</span>
                      <p class="text-xs text-muted">{rtesti.esclusioni?.[doc.motivo] ?? doc.motivo}</p>
                    </li>
                  {/each}
                </ul>
              {:else}
                <p class="text-sm text-muted">{$t('bilancio.riscontro.liste.none')}</p>
              {/if}
            </div>
          </details>

          {#each [['inAttesa', r.inAttesa], ['nonEur', r.nonEur]] as [key, docs]}
            <details class="disclosure">
              <summary><span class="disclosure-label">{$t(`bilancio.riscontro.liste.${key}`)}<span class="tab-count">{docs.length}</span></span></summary>
              <div class="disclosure-content">
                <p class="text-xs text-muted mb-2">{$t(`bilancio.riscontro.liste.${key}Intro`)}</p>
                {#if docs.length}
                  <ul class="bilancio-docs">
                    {#each docs as doc, index (`${documentKey(doc)}-${index}`)}
                      <li>
                        <a href={`/invoices/${doc.id}`} use:link class="invoice-link">{docLabel(doc)}</a>
                        <span class="text-xs text-muted">{docMeta(doc)}</span>
                      </li>
                    {/each}
                  </ul>
                {:else}
                  <p class="text-sm text-muted">{$t('bilancio.riscontro.liste.none')}</p>
                {/if}
              </div>
            </details>
          {/each}
        </section>
      {/if}
    </div>

    <!-- ── Nota integrativa ─────────────────────────────────────────── -->
    <div id="bilancio-panel-nota" role="tabpanel" aria-labelledby="bilancio-tab-nota" tabindex="0" hidden={tab !== 'nota'}>
      {#if statements}
        <section class="mb-10" aria-labelledby="bilancio-anagrafica-title">
          <div class="section-heading">
            <h2 id="bilancio-anagrafica-title">{$t('bilancio.nota.anagrafica')}</h2>
          </div>
          {#if statements.anagrafica?.length}
            <dl class="bilancio-informative">
              {#each statements.anagrafica as line (line.concept)}
                <div>
                  <dt>{line.label}</dt>
                  <dd class="break-words">{anagraficaValue(line)}</dd>
                </div>
              {/each}
            </dl>
          {:else}
            <p class="text-sm text-muted">{$t('bilancio.nota.emptyAnagrafica')}</p>
          {/if}
        </section>

        <section class="mb-6" aria-labelledby="bilancio-testi-title">
          <div class="section-heading">
            <h2 id="bilancio-testi-title">{$t('bilancio.nota.testi')}</h2>
          </div>
          {#if statements.testi?.length}
            <p class="text-xs text-muted mb-4 measure-wide">{$t('bilancio.nota.testiIntro')}</p>
            <!-- Plain text only: the filer's XHTML was reduced to text on the
                 server and is rendered here as text nodes, never as markup. -->
            {#each statements.testi as block, index (`${block.concept}-${index}`)}
              <article class="bilancio-text-block measure-wide" aria-labelledby="bilancio-testo-{index}">
                <h3 id="bilancio-testo-{index}" class="font-bold text-heading text-sm">
                  {block.label}
                  {#if block.periodRole === 'precedente'}<span class="text-xs text-muted">({$t('bilancio.nota.precedente')})</span>{/if}
                </h3>
                {#each textParagraphs(block.testo) as paragraph}
                  <p class="bilancio-paragraph">{paragraph}</p>
                {/each}
              </article>
            {/each}
          {:else}
            <p class="text-sm text-muted">{$t('bilancio.nota.empty')}</p>
          {/if}
        </section>
      {/if}
    </div>

    <!-- ── Modello ISA ───────────────────────────────────────────────── -->
    <div id="bilancio-panel-isa" role="tabpanel" aria-labelledby="bilancio-tab-isa" tabindex="0" hidden={tab !== 'isa'}>
      {#if isaOpened}
        <BilancioIsa {companyId} {bilancio} role={$capabilities?.role} />
      {/if}
    </div>

    {#if permissions.upload && bilancio.live}
      <BilancioUploadSheet open={uploadOpen} {companyId} canReplace={false} repairFor={bilancio}
        on:close={() => (uploadOpen = false)} on:done={onRepaired} />
    {/if}
  {/if}
</Layout>

<style>
  .bilancio-tabs { flex-wrap: wrap; column-gap: 24px; }
  .bilancio-meta .summary-note { font-size: 0.875rem; color: var(--color-text-heading); }
  .bilancio-kpi-value {
    font-family: var(--wd-kpi-font);
    font-size: 1.25rem;
    line-height: 1.3;
  }

  .bilancio-table-wrap { max-width: 100%; }
  .bilancio-row { border-bottom: var(--wd-border-width-hairline) solid var(--table-border); }
  .bilancio-row th, .bilancio-row td { vertical-align: top; white-space: normal; }
  .bilancio-ratio-label {
    min-width: 12rem;
    color: var(--color-text-heading);
    font-weight: normal;
    text-align: left;
  }
  .bilancio-flag { display: inline-flex; align-items: center; gap: 4px; margin-top: 4px; }

  .bilancio-voce {
    padding-left: calc(1rem + var(--bilancio-depth, 0) * 0.875rem);
    color: var(--color-text-body);
    font-weight: normal;
    text-align: left;
  }
  .bilancio-voce-code {
    margin-right: 6px;
    color: var(--color-text-muted);
    font-size: 0.75rem;
  }
  .bilancio-heading-row .bilancio-voce { color: var(--color-text-heading); font-weight: var(--wd-label-weight); }
  .bilancio-total-row .bilancio-voce,
  .bilancio-total-row .bilancio-cell-figure { color: var(--color-text-heading); font-weight: var(--wd-label-weight); }
  .bilancio-cell { min-width: 9rem; }
  .bilancio-cell-figure {
    color: var(--color-text-heading);
    font-variant-numeric: var(--wd-figures-tabular);
    white-space: nowrap;
  }
  .bilancio-cell-uncertain { color: var(--color-text-body); }
  .bilancio-cell-muted { color: var(--color-text-muted); font-size: 0.75rem; }
  .bilancio-cell-detail {
    display: block;
    color: var(--color-text-muted);
    font-size: 0.75rem;
  }
  .bilancio-cell .bilancio-flag { justify-content: flex-end; }
  .bilancio-solved {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 2px 4px;
    margin-top: 2px;
  }
  .bilancio-serie-table :global(.bilancio-value-line) { flex-wrap: nowrap; }

  .bilancio-serie-table th, .bilancio-serie-table td { white-space: nowrap; }

  .bilancio-assumption {
    padding: 14px 16px;
    border: var(--wd-border-width-hairline) solid var(--color-border-default);
    border-radius: var(--wd-radius-surface);
  }
  .bilancio-aliquota-input { width: 8rem; }

  .bilancio-crosscheck {
    display: grid;
    gap: 16px;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 20rem), 1fr));
  }
  .bilancio-crosscheck-card { min-width: 0; }
  .bilancio-figures {
    display: grid;
    gap: 12px 16px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .bilancio-figures dt, .bilancio-informative dt {
    color: var(--color-text-muted);
    font-size: 0.75rem;
  }
  .bilancio-figures dd, .bilancio-informative dd {
    margin: 2px 0 0;
    color: var(--color-text-heading);
    font-variant-numeric: var(--wd-figures-tabular);
  }
  .bilancio-informative {
    display: grid;
    gap: 14px 24px;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 14rem), 1fr));
  }
  .bilancio-explanations {
    display: grid;
    gap: 20px 28px;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 17rem), 1fr));
  }
  .bilancio-bullets {
    padding-left: 1.1rem;
    list-style: disc;
  }
  .bilancio-bullets li + li { margin-top: 6px; }
  .bilancio-docs { padding: 0; list-style: none; }
  .bilancio-docs li {
    padding: 8px 0;
    border-bottom: var(--wd-border-width-hairline) solid var(--color-border-default);
    overflow-wrap: anywhere;
  }
  .bilancio-derived { display: grid; gap: 12px; padding: 0; list-style: none; }
  .bilancio-text-block + .bilancio-text-block { margin-top: 22px; }
  .bilancio-paragraph {
    margin-top: 8px;
    font-size: 0.875rem;
    white-space: pre-line;
    overflow-wrap: anywhere;
  }

  /* Phones: the ratio tables become one card per ratio, each value with its
     year beside it; the statements keep their columns but tighten. */
  @media (width < 42.5rem) {
    .bilancio-ratio-table thead {
      position: absolute;
      width: 1px;
      height: 1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
    }
    .bilancio-ratio-table, .bilancio-ratio-table tbody { display: block; width: 100%; }
    .bilancio-ratio-table tr { display: block; padding: 10px 0; }
    .bilancio-ratio-table th[scope='row'] { display: block; min-width: 0; padding: 0 4px 6px; }
    .bilancio-ratio-table td {
      display: grid;
      grid-template-columns: minmax(6.5rem, 38%) minmax(0, 1fr);
      gap: 0 10px;
      padding: 4px;
      text-align: left;
    }
    .bilancio-ratio-table td::before {
      content: attr(data-label);
      color: var(--color-text-muted);
      font-size: 12px;
    }

    /* Statements: one block per line, the label across the top and each
       year's value under it with the year named, so an eight-digit amount
       never has to share a row with a long label. */
    .bilancio-statement thead {
      position: absolute;
      width: 1px;
      height: 1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
    }
    .bilancio-statement, .bilancio-statement tbody { display: block; width: 100%; }
    .bilancio-statement tr {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 2px 12px;
      padding: 8px 4px 8px calc(4px + var(--bilancio-depth, 0) * 0.5rem);
    }
    .bilancio-statement th[scope='row'] { grid-column: 1 / -1; padding: 0; }
    .bilancio-statement td { display: block; min-width: 0; width: auto; padding: 0; text-align: left; }
    .bilancio-statement td::before {
      content: attr(data-label);
      display: block;
      color: var(--color-text-muted);
      font-size: 11px;
    }
    .bilancio-statement .bilancio-heading-row td { display: none; }
    .bilancio-statement :is(.bilancio-solved, .bilancio-flag) { justify-content: flex-start; }
    .bilancio-figures { grid-template-columns: minmax(0, 1fr); }
  }
</style>
